import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedPatients,
  useRestorePatients,
  usePermanentlyDeletePatients,
  useForceDeletePatients,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { joinFullName } from '../../utils/patientName';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { AlertDialog } from '../../components/ui/AlertDialog';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  UserCircle,
  Phone,
} from 'lucide-react';

const LIMIT = 20;

export function PatientsTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; patient?: any } | null>(null);
  const [blockedDialog, setBlockedDialog] = useState<{ title: string; messages: string[]; ids?: string[] } | null>(null);
  const [forceTarget, setForceTarget] = useState<{ ids: string[]; label: string } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedPatients(page, LIMIT, {
    search: debouncedSearch.trim() || undefined,
  });
  const restorePatients = useRestorePatients();
  const permanentDelete = usePermanentlyDeletePatients();
  const forceDelete = useForceDeletePatients();

  const patients = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const patientName = (patient: any) => joinFullName(patient) || 'Unknown';

  const allSelected = patients.length > 0 && patients.every((p: any) => selectedIds.includes(p.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !patients.some((p: any) => p.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...patients.map((p: any) => p.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      const res: any = await restorePatients.mutateAsync([id]);
      const blocked = res?.data?.blocked ?? [];
      if (blocked.length > 0) {
        toast.error(blocked[0]?.reason || 'This patient could not be restored');
        return;
      }
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Patient restored');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore patient');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restorePatients.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      if (restored > 0) toast.success(`Restored ${restored} patient${restored === 1 ? '' : 's'}`);
      if (blocked.length > 0) {
        toast.error(`${blocked.length} patient(s) could not be restored: ${blocked[0]?.reason ?? ''}`);
      } else if (restored === 0) {
        toast.error('No patients were restored');
      }
      setSelectedIds([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore patients');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        const res: any = await permanentDelete.mutateAsync([confirmTarget.patient.id]);
        const blocked = res?.data?.blocked ?? [];
        if (blocked.length > 0) {
          setBlockedDialog({
            title: 'Cannot delete permanently',
            ids: [confirmTarget.patient.id],
            messages: [
              `${patientName(confirmTarget.patient)} — ${blocked[0]?.reason ?? 'This patient could not be permanently deleted.'}`,
            ],
          });
          return;
        }
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.patient.id));
        toast.success('Patient permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? 0;
        const blocked = res?.data?.blocked ?? [];
        if (deleted > 0) toast.success(`Permanently deleted ${deleted} patient${deleted === 1 ? '' : 's'}`);
        if (blocked.length > 0) {
          const nameById = new Map<string, string>(patients.map((p: any) => [p.id, patientName(p)]));
          setBlockedDialog({
            title: `Cannot permanently delete ${blocked.length} patient${blocked.length === 1 ? '' : 's'}`,
            ids: blocked.map((b: any) => b.id),
            messages: blocked.map((b: any) => `${nameById.get(b.id) ?? b.id} — ${b.reason}`),
          });
        } else if (deleted === 0) {
          setBlockedDialog({
            title: 'No patients were deleted',
            ids: selectedIds,
            messages: ['None of the selected patients could be permanently deleted.'],
          });
        }
        setSelectedIds([]);
      }
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot delete permanently',
        ids: confirmTarget.mode === 'single' ? [confirmTarget.patient.id] : selectedIds,
        messages: [err?.response?.data?.message || 'The patient(s) could not be permanently deleted.'],
      });
    } finally {
      setConfirmTarget(null);
    }
  };

  const handleForceDelete = async () => {
    if (!forceTarget) return;
    try {
      const res: any = await forceDelete.mutateAsync(forceTarget.ids);
      const deleted = res?.data?.deleted?.length ?? 0;
      const notFound = res?.data?.notFound?.length ?? 0;
      if (deleted > 0) {
        toast.success(
          `Permanently deleted ${deleted} patient${deleted === 1 ? '' : 's'} and all linked records`,
        );
      }
      if (notFound > 0) {
        toast.error(`${notFound} patient(s) were not found in trash`);
      }
      setSelectedIds((prev) => prev.filter((id) => !forceTarget.ids.includes(id)));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete patient(s) and linked records');
    } finally {
      setForceTarget(null);
      setBlockedDialog(null);
    }
  };

  const busy = restorePatients.isPending || permanentDelete.isPending || forceDelete.isPending;

  const blockedIds = blockedDialog?.ids ?? [];

  const openForceDelete = () => {
    if (blockedIds.length === 0) return;
    setForceTarget({
      ids: blockedIds,
      label: blockedIds.length === 1 ? 'this patient' : `${blockedIds.length} patients`,
    });
    setBlockedDialog(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/patients')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Patients Trash</h1>
            <p className="text-xs text-text-muted">Restore patients or delete them permanently</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trashed patients by name, mobile, or registration number..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restorePatients.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed patients</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={patients.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Reg. #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {patients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <UserCircle className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed patients match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  patients.map((patient: any) => (
                    <tr key={patient.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(patient.id)}
                          onChange={() => toggleSelectOne(patient.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <UserCircle size={16} className="text-primary" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-text">{patientName(patient)}</div>
                            <p className="text-xs text-text-muted">{patient.gender || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-text">{patient.registrationNumber || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text">
                          <Phone size={14} className="text-text-muted" />
                          {patient.mobileNumber || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {patient.deletedAt ? new Date(patient.deletedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRestore(patient.id)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Restore"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', patient })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed patients"
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete patients permanently' : 'Delete patient permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected patient(s)? This cannot be undone. Patients still linked to clinical, billing, or laboratory records cannot be deleted.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.patient ? patientName(confirmTarget.patient) : ''}</strong>?
              This cannot be undone. Patients still linked to clinical, billing, or laboratory records cannot be deleted.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      <AlertDialog
        open={blockedDialog !== null}
        title={blockedDialog?.title ?? 'Cannot delete permanently'}
        description="These records are still referenced by other data, so they cannot be permanently deleted the normal way. You can delete them anyway below — this also removes every linked record."
        messages={blockedDialog?.messages}
        actionLabel={blockedIds.length > 0 ? 'Delete Anyway' : undefined}
        onAction={blockedIds.length > 0 ? openForceDelete : undefined}
        onClose={() => setBlockedDialog(null)}
      />

      <ConfirmDialog
        open={forceTarget !== null}
        title="Delete anyway — all linked records"
        description={
          <>
            This will permanently delete <strong>{forceTarget?.label ?? 'the selected record(s)'}</strong> and every
            record linked to them — visits, clinical notes, lab &amp; radiology orders, prescriptions, pharmacy sales,
            invoices, payments, and ledger/accounting entries. This action cannot be undone.
          </>
        }
        confirmLabel="Delete anyway"
        loading={forceDelete.isPending}
        onConfirm={handleForceDelete}
        onCancel={() => setForceTarget(null)}
      />
    </div>
  );
}
